import { API_PATHS } from "../constants/api-paths";
import { CART_CONSTANTS } from "../constants/cart";
import { APP_SRC, COMMON_SSO_MOBILE } from "../constants/sso";
import {
  clearCacheForFunction,
  clearCacheForKey,
  createCacheAsync,
} from "@repo/shared-utils/cache";
import { getItemsInCart } from "@repo/shared-utils/cart";
import { decryptLocalStorageMobileNo } from "@repo/shared-utils/crypto";
import { getParams } from "@repo/shared-utils/common-utils";

import { type IResponse, encryptedRequest } from "../core/api.client";

export interface ICartItemDetails {
  mobileNo: string;
  appId: string;
  prod: string;
  prodDesc: string;
  appLink: string;
  img: string;
  channel: string;
  prodProps: string;
  resumeFlag: boolean;
  appVersion: string;
  variant: string;
  roi: string;
  amt: string;
  tenure: string;
  skuId: string;
  emi: string;
  id: string;
  recordTime?: number;
  cartDeletionNotAllowed?: boolean;
  eventCode?: string;
  cartHidden?: boolean;
}

interface ICartApiResponse {
  cartCount?: number;
  cartDetails: ICartItemDetails[];
}

export interface ICmsCartProducts {
  prodcode: string;
  specialization: string;
  loanvariant: string;
  proddesc: string;
  imagelink: string;
  "in-app-link": string;
  "web-link": string;
  xaopImageLink: string;
  xaopImageLinkWeb: string;
  isAppEnabled: boolean;
  isWebEnabled: boolean;
  ctaName: string;
  isTextOnImage: boolean;
  productTextColor: string;
  prefixField1: string;
  prefixField2: string;
  usp: IUsp[];
}

interface IUsp {
  uspName: string;
  uspBackgroundColor: string;
  uspTextColor: string;
}

export interface ICartAndCmsCartResponse extends ICartItemDetails, ICmsCartProducts {}

export const getCart = async (): Promise<IResponse<ICartApiResponse>> => {
  const localStorageMobileNo = localStorage.getItem(COMMON_SSO_MOBILE);
  const mobileNo = await decryptLocalStorageMobileNo(localStorageMobileNo || "");
  const channel = getParams(APP_SRC) || "";
  const response = await createCacheAsync(
    `${API_PATHS.cartRead}-${mobileNo}`,
    encryptedRequest<ICartApiResponse, { mobileNo: string; channel: string }>,
    {
      method: "POST",
      url: API_PATHS.cartRead,
      data: {
        mobileNo,
        channel,
      },
    }
  );

  return response;
};

export const getCmsCartProducts = async (): Promise<IResponse<ICmsCartProducts[]>> => {
  const localStorageMobileNo = localStorage.getItem(COMMON_SSO_MOBILE);
  const mobileNo = await decryptLocalStorageMobileNo(localStorageMobileNo || "");
  const prodcode = CART_CONSTANTS.ALL;
  const channel = CART_CONSTANTS.WEB;
  const cartProducts = await createCacheAsync(
    `${API_PATHS.cmsCartProducts}-${mobileNo}`,
    encryptedRequest<ICmsCartProducts[], { mobileNo: string; prodcode: string; channel: string }>,
    {
      method: "POST",
      url: API_PATHS.cmsCartProducts,
      data: {
        mobileNo,
        prodcode,
        channel,
      },
    }
  );

  return cartProducts;
};
export interface IApiLoansReadRequest {
  mobileNo: string;
  appId: string;
  product:
    | "PLCS"
    | "SALPL"
    | "PROF"
    | "BL"
    | "BLSE"
    | "UCF"
    | "HL"
    | "HL_BT"
    | "LAS_SHARES"
    | "LAS_MUTUALFUND"
    | "GL"
    | "PLCARD"
    | "LAP"
    | "LAPBT"
    | "MFI"
    | "BEMFR"
    | "LAIP"
    | "PEMFR"
    | "BALIC"
    | "TWL"
    | "UTL_ALL"
    | "UTL_LAT"
    | "UTL_BT";
}

interface IApiLoansReadResponse {
  id: string;
  journeyName: string;
  journeyType: string;
  eventCode: string;
  offerProduct: string;
  offerId: string;
  requestedLoanAmt: string;
  requestedROI: string;
  requestedIRR: string;
  requestedLoanTenor: string;
  cartId: string;
  startNewApplicationFlag: boolean;
  appId: string;
}

export async function postLoansRead(
  data: IApiLoansReadRequest
): Promise<IResponse<IApiLoansReadResponse>> {
  const response = await createCacheAsync(
    `${API_PATHS.loansRead}-${data.mobileNo}-${data.appId}-${data.product}`,
    encryptedRequest<IApiLoansReadResponse, IApiLoansReadRequest>,
    {
      method: "POST",
      url: API_PATHS.loansRead,
      data,
    }
  );

  if (response.statusCode !== 90) {
    throw new Error(response.message);
  }

  return response;
}

export const deleteCartItem = async (appId: string): Promise<IResponse<IApiResponseCartDelete>> => {
  const localStorageMobileNo = localStorage.getItem(COMMON_SSO_MOBILE);
  const mobileNo = await decryptLocalStorageMobileNo(localStorageMobileNo || "");
  try {
    const response = await encryptedRequest<IApiResponseCartDelete>({
      method: "POST",
      url: API_PATHS.cartDelete,
      data: {
        mobileNo,
        appId,
        channel: CART_CONSTANTS.CHANNEL,
      },
    });
    return response;
  } catch (err) {
    console.error(err);
    throw err;
  }
};

interface IApiResponseCartDelete {
  data: null;
  statusCode: number;
  status: string;
  message: string;
}

export const deleteCartApi = (mobileNo: string) => {
  clearCacheForKey(
    `${API_PATHS.cartRead}-${mobileNo}`,
    encryptedRequest<ICartApiResponse, { mobileNo: string; channel: string }>
  );
  clearCacheForKey(mobileNo, getCart);
  clearCacheForFunction(getItemsInCart);
};
